import React, { useEffect, useState } from 'react';
import { fmtDate } from '../utils';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import api from '../api';

const STAGE_COLORS = { Prospecting:'#8b5cf6',Qualification:'#3b82f6',Shaping:'#06b6d4','Pre-RFP':'#f59e0b',Proposal:'#f97316',Evaluation:'#ec4899',Won:'#16a34a',Lost:'#dc2626','No Bid':'#94a3b8','On Hold':'#64748b' };
const PRIORITY_COLORS = { Critical:'#dc2626',High:'#f97316',Medium:'#f59e0b',Low:'#16a34a' };
const Q_COLORS = ['#3b82f6','#2563eb','#1d4ed8','#1e40af'];

function smartMoney(val) {
  const n = Number(val);
  if (!val && val !== 0 || isNaN(n)) return '—';
  if (n >= 1e9) return `$${(n/1e9).toFixed(1).replace(/\.0$/,'')}B`;
  if (n >= 1e6) return `$${(n/1e6).toFixed(1).replace(/\.0$/,'')}M`;
  if (n >= 1e3) return `$${(n/1e3).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

const QuarterTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'white',border:'1px solid var(--gray-200)',borderRadius:8,padding:'10px 14px',fontSize:13,boxShadow:'0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ fontWeight:700,marginBottom:4,color:'var(--gray-800)' }}>{label}</div>
      <div style={{ color:'#2563eb' }}>{smartMoney((payload[0]?.value||0)*1000)} TCV</div>
      <div style={{ color:'var(--gray-500)',fontSize:12 }}>{payload[1]?.value||0} opportunities</div>
    </div>
  );
};

export default function Reports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/summary')
      .then(r => setData(r.data))
      .catch(err => console.error('Dashboard fetch failed:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding:40,color:'var(--gray-400)' }}>Loading reports...</div>;
  if (!data) return <div style={{ padding:40,color:'var(--danger)' }}>Failed to load report data.</div>;

  const { kpis={}, oppsByStage=[], oppsByPriority=[], topAccounts=[], monthlyActivity=[], quarterlyPipeline=[] } = data;

  const pipelineValue = Number(kpis.pipelineValue)||0;
  const wonValue = Number(kpis.wonValue)||0;
  const wonCount = Number(kpis.wonCount)||0;
  const lostCount = (oppsByStage).find(s=>s.stage==='Lost')?.count||0;
  const winRate = wonCount+lostCount>0 ? Math.round((wonCount/(wonCount+lostCount))*100) : 0;

  // Build quarterly chart from API data
  const quarterlyData = (() => {
    if (quarterlyPipeline && quarterlyPipeline.length > 0) {
      return quarterlyPipeline.map(q => ({
        label: `${q.quarter} ${q.year}`,
        tcv: Math.round((q.total_tcv||0)/1000),
        count: q.count||0
      }));
    }
    // Fallback: distribute by stage groups
    const year = new Date().getFullYear();
    const stageGroups = [
      { q:`Q1 ${year}`, stages:['Prospecting','Qualification'] },
      { q:`Q2 ${year}`, stages:['Shaping','Pre-RFP'] },
      { q:`Q3 ${year}`, stages:['Proposal'] },
      { q:`Q4 ${year}`, stages:['Evaluation','Won'] },
    ];
    return stageGroups.map(g => ({
      label: g.q,
      tcv: Math.round(oppsByStage.filter(s=>g.stages.includes(s.stage)).reduce((s,o)=>s+(o.value||0),0)/1000),
      count: oppsByStage.filter(s=>g.stages.includes(s.stage)).reduce((s,o)=>s+(o.count||0),0)
    }));
  })();

  const stagePieData = oppsByStage.filter(s=>parseInt(s.count)>0).map(s=>({ name:s.stage,value:parseInt(s.count)||0,tcv:parseFloat(s.value)||0 }));
  const priorityData = oppsByPriority.map(s=>({ name:s.priority,value:parseInt(s.count)||0 }));
  const activityData = monthlyActivity.map(m=>({ month:m.month,count:parseInt(m.count)||0 }));

  return (
    <>
      <div className="page-header">
        <span className="page-title">Reports & Analytics</span>
        <span style={{ fontSize:12,color:'var(--gray-400)' }}>Live · {new Date().getFullYear()}</span>
      </div>
      <div className="page-body">
        {/* KPI Summary */}
        <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:24 }}>
          {[
            { label:'Total Pipeline TCV', value:smartMoney(pipelineValue), sub:`${kpis.totalOpportunities||0} active opps` },
            { label:'Won TCV', value:smartMoney(wonValue), sub:`${wonCount} deals closed won` },
            { label:'Win Rate', value:`${winRate}%`, sub:'Won vs closed' },
            { label:'Active Contract Value', value:smartMoney(kpis.activeContractValue||0), sub:`${kpis.activeContracts||0} contracts` },
          ].map(({ label,value,sub }) => (
            <div key={label} className="card" style={{ padding:'18px 20px' }}>
              <div style={{ fontSize:11,color:'var(--gray-500)',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:6 }}>{label}</div>
              <div style={{ fontSize:22,fontWeight:700 }}>{value}</div>
              <div style={{ fontSize:12,color:'var(--gray-500)',marginTop:4 }}>{sub}</div>
            </div>
          ))}
        </div>

        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:20 }}>
          {/* ✅ Total Pipeline Value by Quarter — Bar Chart */}
          <div className="card">
            <div className="card-header"><span className="card-title">Total Pipeline Value by Quarter</span></div>
            <div className="card-body" style={{ paddingTop:8 }}>
              {quarterlyData.every(q=>q.tcv===0)
                ? <div className="empty-state" style={{ padding:'30px 0' }}><div className="empty-state-icon">📊</div><div className="empty-state-text">No pipeline with close dates set yet</div></div>
                : <ResponsiveContainer width="100%" height={230}>
                    <BarChart data={quarterlyData} margin={{ top:8,right:8,left:-5,bottom:4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" />
                      <XAxis dataKey="label" tick={{ fontSize:12 }} />
                      <YAxis tick={{ fontSize:10 }} tickFormatter={v=>`$${v}K`} />
                      <Tooltip content={<QuarterTooltip />} />
                      <Bar dataKey="tcv" name="TCV ($K)" radius={[6,6,0,0]}>
                        {quarterlyData.map((_,i)=><Cell key={i} fill={Q_COLORS[i%4]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
              }
            </div>
          </div>

          {/* Pipeline by Stage Pie */}
          <div className="card">
            <div className="card-header"><span className="card-title">Pipeline by Stage</span></div>
            <div className="card-body" style={{ paddingTop:4 }}>
              {stagePieData.length===0
                ? <div className="empty-state" style={{ padding:'30px 0' }}><div className="empty-state-text">No pipeline data yet</div></div>
                : <ResponsiveContainer width="100%" height={230}>
                    <PieChart>
                      <Pie data={stagePieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" nameKey="name" fontSize={10}
                        label={({ name,value })=>`${name} (${value})`} labelLine>
                        {stagePieData.map((e,i)=><Cell key={i} fill={STAGE_COLORS[e.name]||'#6366f1'} />)}
                      </Pie>
                      <Tooltip formatter={(v,n,p)=>[`${v} opps · ${smartMoney(p.payload.tcv)}`,p.payload.name]} />
                    </PieChart>
                  </ResponsiveContainer>
              }
            </div>
          </div>
        </div>

        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 360px',gap:20 }}>
          {/* Opps by Priority */}
          <div className="card">
            <div className="card-header"><span className="card-title">Opportunities by Priority</span></div>
            <div className="card-body">
              {priorityData.length===0
                ? <div className="empty-state"><div className="empty-state-text">No data</div></div>
                : <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={priorityData} margin={{ top:4,right:8,left:-10,bottom:4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" />
                      <XAxis dataKey="name" tick={{ fontSize:12 }} />
                      <YAxis tick={{ fontSize:10 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="value" radius={[4,4,0,0]}>
                        {priorityData.map((e,i)=><Cell key={i} fill={PRIORITY_COLORS[e.name]||'#94a3b8'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
              }
            </div>
          </div>

          {/* Activity over time */}
          <div className="card">
            <div className="card-header"><span className="card-title">Activity Over Time</span></div>
            <div className="card-body">
              {activityData.length===0
                ? <div className="empty-state"><div className="empty-state-text">No activity yet</div></div>
                : <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={activityData} margin={{ top:4,right:8,left:-10,bottom:0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" />
                      <XAxis dataKey="month" tick={{ fontSize:10 }} />
                      <YAxis tick={{ fontSize:10 }} allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#2563eb" strokeWidth={2} dot={{ r:4 }} />
                    </LineChart>
                  </ResponsiveContainer>
              }
            </div>
          </div>

          {/* Top accounts with year */}
          <div className="card">
            <div className="card-header"><span className="card-title">Top Accounts by Pipeline</span></div>
            <table>
              <thead><tr><th>#</th><th>Account</th><th>Type</th><th>Pipeline</th></tr></thead>
              <tbody>
                {topAccounts.map((acc,i) => (
                  <tr key={acc.id}>
                    <td style={{ color:'var(--gray-400)',fontSize:12 }}>{i+1}</td>
                    <td style={{ fontSize:13,fontWeight:500 }}>{acc.name}</td>
                    <td style={{ fontSize:12 }}>{acc.customer_type}</td>
                    <td style={{ fontWeight:600,color:'var(--success)',fontSize:13 }}>{smartMoney(acc.pipeline)}</td>
                  </tr>
                ))}
                {topAccounts.length===0 && <tr><td colSpan={4}><div className="empty-state" style={{ padding:20 }}><div className="empty-state-text">No data</div></div></td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
